import React from "react";

import { Button, useTheme, withStyles } from "@material-ui/core";
import { ExpandLess, ExpandMore, GetApp } from "@material-ui/icons";
import {
    DataGridPro,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    useGridApiRef
} from "@mui/x-data-grid-pro";

import PercentageIndicator from "components/visuals/PercentageIndicator";
import Spacer from "components/Spacer";
import { selectMonthPeriodLabel, selectNumberOfPeriods } from "modules/customer/insights/portfolioNew/portfolioSlice";
import { selectHistoryAndForecastRevenueGrowthByStore } from "modules/customer/insights/portfolioNew/revenue/revenueSlice";
import { useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const StyledDataGrid = withStyles(theme => ({
    root: {
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        "& .MuiCheckbox-root": {
            color: theme.palette.text.primary,
            "&.Mui-checked": {
                //@ts-ignore
                color: theme.palette.tertiary.main,
            },
        },
        "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.16)",
            },
        },
        "& .MuiDataGrid-cell:focus": {
            outline: "none"
        },
        "& .MuiDataGrid-cell:focus-within": {
            outline: "none"
        }
    }
}))(DataGridPro);

const HistoryAndForecastRevenueGrowthByStore: React.FC = () => {
    const theme = useTheme();
    const monthPeriodLabel = useAppSelector(selectMonthPeriodLabel);
    const numberOfPeriods = useAppSelector(selectNumberOfPeriods);
    const historyAndForecastRevenueGrowthByStore = useAppSelector(selectHistoryAndForecastRevenueGrowthByStore);

    const columns: GridColDef[] = [
        {
            field: "name",
            flex: 1,
            headerName: "Store name",
            sortable: true,
        },
        {
            field: "revenueLTM",
            flex: 1,
            headerName: `Revenue in the past ${numberOfPeriods} ${monthPeriodLabel}s`,
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1),
        },
        {
            field: "revenuePY",
            flex: 1,
            headerName: `Revenue in the past ${numberOfPeriods} to ${numberOfPeriods + numberOfPeriods} ${monthPeriodLabel}s`,
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1),
        },
        {
            field: "forecastRevenue",
            flex: 1,
            headerName: `Forecasted revenue in the next ${numberOfPeriods} ${monthPeriodLabel}s`,
            sortable: true,
            renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1),
        },
        {
            field: "percentChangeRevenueLTMVsRevenuePY",
            flex: 1,
            headerName: `% change last ${numberOfPeriods} ${monthPeriodLabel}s vs previous ${numberOfPeriods} to ${numberOfPeriods + numberOfPeriods} ${monthPeriodLabel}s`,
            sortable: true,
            renderCell: (params) => <PercentageIndicator loading={false} error={false} align="right" showIndicator={false} value={Number(params.value ?? 0)} switchColor={false} noDataMessage={"N/A"} />
        },
        {
            field: "percentChangeForecastRevenueVsRevenueLTM",
            flex: 1,
            headerName: `% change next ${numberOfPeriods} ${monthPeriodLabel}s vs last ${numberOfPeriods} ${monthPeriodLabel}s`,
            sortable: true,
            renderCell: (params) => <PercentageIndicator loading={false} error={false} align="right" showIndicator={false} value={Number(params.value ?? 0)} switchColor={false} noDataMessage={"N/A"} />
        }

    ];

    const columndsWithWidth = columns.map(column => {
        return {
            ...column,
            minWidth: 200
        };
    });

    const apiRef = useGridApiRef();

    const handleExport = () => {
        apiRef.current.exportDataAsCsv({ fileName: "History and forecast revenue growth by store" });
    };

    const CustomExportButton = () => {
        return (
            <Button
                // @ts-ignore
                style={{ color: theme.palette.tertiary.main }}
                startIcon={<GetApp />}
                onClick={handleExport}
                size="small"
            >
                Export
            </Button>
        );
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer style={{ paddingRight: theme.spacing(2) }}>
                <GridToolbarColumnsButton style={{ color: theme.palette.text.primary }} />
                <GridToolbarFilterButton style={{ color: theme.palette.text.primary }} />
                <Spacer />
                <CustomExportButton />
            </GridToolbarContainer>
        );
    }

    return (
        <StyledDataGrid
            rows={historyAndForecastRevenueGrowthByStore}
            columns={columndsWithWidth}
            checkboxSelection
            disableSelectionOnClick
            loading={false}
            components={{
                Toolbar: CustomToolbar,
                ColumnSortedAscendingIcon: ExpandLess,
                ColumnSortedDescendingIcon: ExpandMore
            }}
            sortingOrder={["asc", "desc"]}
            apiRef={apiRef}
        />
    );
};

export default HistoryAndForecastRevenueGrowthByStore;
